import { ref, watch, type Ref } from 'vue'

const MIN_POSITION_SECONDS = 5
const MAX_POSITION_PERCENT = 0.95

interface UseServerResumeOptions {
  /** Resume position resolved server-side (from the playback info), in seconds. */
  resumePositionSeconds: Ref<number | null | undefined>
  /** Asset duration once known, to discard near-start / near-end positions. */
  duration: Ref<number>
}

/**
 * Exposes the server-side resume position for the "Resume playback?" prompt.
 *
 * Replaces the old localStorage-based resume: the position now comes from the
 * backend (keyed by the viewer — user account or the durable viewer_id cookie),
 * so it is consistent across devices for logged-in viewers and survives a cache
 * clear for anonymous ones. Writing progress is handled separately by the
 * playback heartbeat; this composable only reads the resume point.
 *
 * Returns the same `{ savedPosition, clearSavedPosition }` shape as the previous
 * resume composable so the player UI is unchanged.
 */
export function useServerResume(options: UseServerResumeOptions) {
  const { resumePositionSeconds, duration } = options

  const savedPosition = ref<number | null>(resumePositionSeconds.value ?? null)

  watch(resumePositionSeconds, (value) => {
    savedPosition.value = value ?? null
  })

  // Discard positions that are too early or too close to the end once duration is known.
  watch(duration, (dur) => {
    if (savedPosition.value !== null && dur > 0) {
      const tooEarly = savedPosition.value < MIN_POSITION_SECONDS
      const tooLate = savedPosition.value >= dur * MAX_POSITION_PERCENT
      if (tooEarly || tooLate) {
        savedPosition.value = null
      }
    }
  })

  function clearSavedPosition(): void {
    savedPosition.value = null
  }

  return {
    savedPosition,
    clearSavedPosition,
  }
}
